import { getFirstTwoChars } from '@geartracker/utils';

import { allLangs, defaultLang } from './config-lang';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------

export function useLocales() {
  const { i18n } = useTranslation();

  const currentLang = useMemo(
    () =>
      allLangs.find(
        (lang) => lang.value === getFirstTwoChars(i18n.resolvedLanguage ?? ''),
      ) || defaultLang,
    [i18n.resolvedLanguage],
  );

  return {
    allLangs,
    currentLang,
  };
}

// ----------------------------------------------------------------------

/**
 * Resolves a model category's display label. Native (global) categories are
 * translated via the `modelCategories.<slug>` key; custom org categories — and
 * any slug without a translation — fall back to their stored name.
 *
 * `t` must be an unprefixed translate function (e.g. `useTranslate().t`).
 */
export function getCategoryLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  category: { name: string; slug: string } | null | undefined,
): string {
  if (!category) {
    return '';
  }
  return t(`modelCategories.${category.slug}`, { defaultValue: category.name });
}

export function useTranslate(keyPrefix?: string) {
  const { t, i18n, ready } = useTranslation('translations', {
    keyPrefix,
  });

  const onChangeLang = useCallback(
    (newlang: string) => {
      void i18n.changeLanguage(newlang);
    },
    [i18n],
  );

  return {
    t,
    i18n,
    ready,
    onChangeLang,
  };
}
